import type { Goal } from './types';
import type { GoalView, GoalInput } from '../../../api/PreformanceManagement/Goals';

// ----------------------
// BACKEND ↔ FRONTEND MAPPING FUNCTIONS
// ----------------------

// Helper functions to map backend enums to frontend strings
export const mapBackendLevelToFrontend = (level: number | string): 'company' | 'team' | 'individual' => {
  if (typeof level === 'string') {
    const lowerLevel = level.toLowerCase();
    if (lowerLevel === 'company') return 'company';
    if (lowerLevel === 'team') return 'team';
    if (lowerLevel === 'individual') return 'individual';
  }

  switch (level) {
    case 1: return 'company';     // GoalLevel.Company
    case 2: return 'team';        // GoalLevel.Team
    case 3: return 'individual';  // GoalLevel.Individual
    default: return 'individual';
  }
};

export const mapBackendStatusToFrontend = (status: number | string): 'on-track' | 'at-risk' | 'completed' | 'overdue' => {
  // Handle string enum names from backend
  if (typeof status === 'string') {
    const statusLower = status.toLowerCase();
    switch (statusLower) {
      case 'ontrack':
      case 'on-track':
      case 'notstarted':
        return 'on-track';
      case 'atrisk':
      case 'at-risk':
        return 'at-risk';
      case 'completed':
        return 'completed';
      case 'overdue':
        return 'overdue';
      default:
        return 'on-track';
    }
  }

  // Handle numeric enum values
  switch (Number(status)) {
    case 1: return 'on-track';   // GoalStatus.OnTrack
    case 2: return 'at-risk';    // GoalStatus.AtRisk
    case 3: return 'completed';  // GoalStatus.Completed
    case 4: return 'overdue';    // GoalStatus.Overdue
    case 5: return 'on-track';   // GoalStatus.NotStarted (map to on-track)
    default: return 'on-track';
  }
};

export const mapBackendPriorityToFrontend = (priority: number | string): 'low' | 'medium' | 'high' => {
  // Handle string enum names from backend
  if (typeof priority === 'string') {
    const priorityLower = priority.toLowerCase();
    switch (priorityLower) {
      case 'low': return 'low';
      case 'medium': return 'medium';
      case 'high': return 'high';
      default: return 'medium';
    }
  }

  // Handle numeric values
  switch (priority) {
    case 0: return 'low';
    case 1: return 'medium';
    case 2: return 'high';
    default: return 'medium';
  }
};

// Helper functions to map frontend strings to backend enums
export const mapFrontendLevelToBackend = (level: string): number => {
  switch (level.toLowerCase()) {
    case 'company': return 1;     // GoalLevel.Company
    case 'team': return 2;        // GoalLevel.Team
    case 'individual': return 3;  // GoalLevel.Individual
    default: return 3;
  }
};

export const mapFrontendStatusToBackend = (status: string): number => {
  switch (status.toLowerCase()) {
    case 'on-track':
    case 'ontrack':
    case 'notstarted':
      return 1;   // GoalStatus.OnTrack
    case 'at-risk':
    case 'atrisk':
      return 2;   // GoalStatus.AtRisk
    case 'completed':
      return 3;   // GoalStatus.Completed
    case 'overdue':
      return 4;   // GoalStatus.Overdue
    default: return 1;
  }
};

export const mapFrontendPriorityToBackend = (priority: string): number => {
  switch (priority.toLowerCase()) {
    case 'low': return 0;
    case 'medium': return 1;
    case 'high': return 2;
    default: return 1;
  }
};

// ----------------------
// CONVERSION FUNCTIONS
// ----------------------

// Format dates without time zone conversion - just take the date part
const formatDateOnly = (dateString: string): string => {
  if (!dateString) return '';
  // Extract just the YYYY-MM-DD part, ignoring time and timezone
  return dateString.split('T')[0];
};

// Convert backend GoalView to frontend Goal interface
export const convertGoalViewToGoal = (goalView: GoalView): Goal => {
  // Handle both numeric and string level values
  const levelValue = typeof goalView.level === 'string'
    ? goalView.level.toLowerCase()
    : goalView.level;

  return {
    id: goalView.id || `goal-${Date.now()}`,
    title: goalView.title || '',
    description: goalView.description || '',
    level: mapBackendLevelToFrontend(levelValue),
    status: mapBackendStatusToFrontend(goalView.status),
    progress: goalView.progress || 0,
    owner: goalView.owner?.name || goalView.owner?.email || 'Unknown',
    ownerId: goalView.owner?.id || goalView.userId,
    department: goalView.department?.name || 'Unknown',
    departmentId: goalView.department?.id ?? goalView.departmentId ?? undefined,
    startDate: formatDateOnly(goalView.startDate),
    dueDate: formatDateOnly(goalView.dueDate),
    milestones: goalView.milestones?.map((milestone: Record<string, unknown>) => ({
      id: (milestone.id as number) || Date.now(),
      title: (milestone.title as string) || '',
      completed: (milestone.isCompleted as boolean) || false,
      dueDate: formatDateOnly(milestone.dueDate as string),
    })) || [],
    parentId: goalView.parentId,
    comments: goalView.comments?.map((comment: Record<string, unknown>) => ({
      id: (comment.id as number) || Date.now(),
      text: (comment.content as string) || (comment.text as string) || '',
      author: ((comment.user as Record<string, unknown>)?.name as string) || ((comment.author as Record<string, unknown>)?.name as string) || 'Unknown',
      authorId: ((comment.user as Record<string, unknown>)?.id as string) || ((comment.author as Record<string, unknown>)?.id as string) || '',
      createdAt: (comment.createdAt as string) || new Date().toISOString(),
      updatedAt: (comment.updatedAt as string) || new Date().toISOString(),
    })) || [],
    tags: goalView.tags || [],
    priority: mapBackendPriorityToFrontend(goalView.priority),
    metricType: goalView.metricType ?? null,
    target: goalView.target ?? null,
    skillIds: goalView.skillIds ?? null,
    // Carried through so the modal can name the linked skills without a search round trip.
    skills: goalView.skills ?? [],
    isArchived: goalView.isDeleted || false,
    createdAt: goalView.createdAt,
    updatedAt: goalView.updatedAt,
    hasActiveBlockers: goalView.hasActiveBlockers || false,
    activeBlockerCount: goalView.activeBlockerCount || 0,
    neededEquipment: goalView.neededEquipment ?? [],
    clarityScore: goalView.clarityScore ?? null,
    clarity: goalView.clarity ?? null,
    clarityScoreUpdatedOn: goalView.clarityScoreUpdatedOn ?? null,
  };
};

// Department ids arrive as numbers, but a cleared form field can still hand us '' or null.
const toDepartmentId = (value: unknown): number | null => {
  if (value === undefined || value === null || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

// Convert frontend Goal to backend GoalInput
export const convertGoalToGoalInput = (goal: Partial<Goal>, currentUserId?: string): GoalInput => {
  return {
    title: goal.title || '',
    description: goal.description || '',
    level: goal.level ? mapFrontendLevelToBackend(goal.level) : mapFrontendLevelToBackend('individual'),
    status: goal.status ? mapFrontendStatusToBackend(goal.status) : mapFrontendStatusToBackend('on-track'),
    progress: goal.progress !== undefined ? goal.progress : 0,
    ownerId: goal.ownerId || currentUserId || '',
    // Only include departmentId if it has a valid value, otherwise null
    departmentId: toDepartmentId(goal.departmentId),
    // Only include categoryId if it has a valid value, otherwise null
    categoryId: goal.categoryId || null,
    // Only include parentId if it has a valid value, otherwise null
    parentId: goal.parentId || null,
    startDate: goal.startDate || new Date().toISOString().split('T')[0],
    dueDate: goal.dueDate || new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    tags: goal.tags || [],
    priority: goal.priority ? mapFrontendPriorityToBackend(goal.priority) : mapFrontendPriorityToBackend('medium'),
    metricType: goal.metricType ?? null,
    target: goal.target ?? null,
    skillIds: goal.skillIds ?? null,
    milestones: goal.milestones?.map(m => ({
      title: m.title,
      description: '',
      isCompleted: m.completed,
      dueDate: m.dueDate,
      order: 0
    })) || [],
    neededEquipment: goal.neededEquipment ?? []
  };
};

// ----------------------
// GOAL LINKING ELIGIBILITY
// ----------------------

// Single source of truth for which goals may be picked as a parent.
export const getEligibleParents = (
  level: Goal['level'],
  departmentId: number | undefined,
  goals: Goal[],
): Goal[] => {
  const active = goals.filter(goal => !goal.isArchived);

  let eligible: Goal[] = [];

  if (level === 'individual') {
    eligible = active.filter(goal => {
      if (goal.level !== 'team') return false;
      // A team goal with no department is company wide, so it can parent anyone.
      if (goal.departmentId === undefined || goal.departmentId === null) return true;
      if (departmentId === undefined || departmentId === null) return false;
      return Number(goal.departmentId) === Number(departmentId);
    });
  } else if (level === 'team') {
    eligible = active.filter(goal => goal.level === 'company');
  }

  return eligible.sort((a, b) => (a.title || '').localeCompare(b.title || ''));
};

export type ParentEligibilityReason = 'none' | 'pick-department' | 'no-team-goals' | 'no-company-goals';

export const getParentEligibilityReason = (
  level: Goal['level'],
  departmentId: number | undefined,
  eligible: Goal[],
): ParentEligibilityReason => {
  if (eligible.length > 0) return 'none';
  if (level === 'individual') {
    return departmentId === undefined || departmentId === null ? 'pick-department' : 'no-team-goals';
  }
  if (level === 'team') return 'no-company-goals';
  return 'none';
};

// ----------------------
// TRANSLATED LABELS
// ----------------------

type TFn = (key: string, options?: Record<string, unknown>) => string;

export const levelLabel = (t: TFn, level: Goal['level']): string => {
  switch (level) {
    case 'company': return t('Company Goal');
    case 'team': return t('Team Goal');
    case 'individual': return t('Individual Goal');
    default: return level;
  }
};

export const statusLabel = (t: TFn, status: Goal['status']): string => {
  const labels: Record<string, string> = {
    'on-track': t('On Track'),
    'at-risk': t('At Risk'),
    'completed': t('Completed'),
    'overdue': t('Overdue'),
  };
  return labels[status] || status;
};
